import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

type ChatResponse = {
  assistant_text: string;
  chart_url?: string | null;
  answerable?: boolean;
  refusal_reason?: string | null;
};

type ChatJobStartResponse = {
  job_id: string;
  state: string;
};

type ChatJobStatusResponse = {
  state: "queued" | "running" | "completed" | "failed";
  latest_status?: {
    stage?: string;
    message?: string;
    at_ms?: number;
  };
  result?: {
    status_code?: number;
    payload?: ChatResponse;
  } | null;
  error?: string | null;
};

export type Message = {
  role: "user" | "system";
  content: string;
  type?: "text" | "image";
  imagePath?: string;
};

const STORAGE_KEY = "beantext-messages";

function App() {
  const layoutVariant = (() => {
    const rawVariant = new URLSearchParams(window.location.search).get("layout");
    const parsed = Number(rawVariant);
    return Number.isFinite(parsed) ? Math.abs(Math.trunc(parsed)) % 6 : 0;
  })();

  const [messages, setMessages] = useState<Message[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  const keyboardOpenRef = useRef(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const logo = new Image();
    logo.src = "/censushublogo.png";
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      const isOpen = keyboardHeight > 50;

      keyboardOpenRef.current = isOpen;
      document.documentElement.style.setProperty("--keyboard-offset", `${keyboardHeight}px`);

      if (!isOpen) {
        chatRef.current?.classList.add("keyboard-closing");
        setTimeout(() => {
          chatRef.current?.classList.remove("keyboard-closing");
        }, 150);
      }
    };

    update();
    vv.addEventListener("resize", update);
    return () => vv.removeEventListener("resize", update);
  }, []);

  const scrollToBottomNow = () => {
    const el = chatRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
    });
  };

  useLayoutEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    if (!keyboardOpenRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        });
      });
    } else {
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    }
  }, [messages]);

  const getCensusMessage = async (
    newMessage: string,
    previousMessages: Message[],
    onStatus: (message: string) => void
  ): Promise<[string, string | null]> => {
    const defaultApiBase = `http://${window.location.hostname}:5000`;
    const apiBase = process.env.REACT_APP_CENSUS_API_BASE_URL ?? defaultApiBase;
    onStatus("Starting query job...");
    const startResponse = await fetch(`${apiBase}/api/census/chat/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt: newMessage,
        session_id: "censushub-web",
        previous_messages: previousMessages,
      }),
    });
    if (!startResponse.ok) {
      throw new Error("Failed to start chat job");
    }
    const startData = (await startResponse.json()) as ChatJobStartResponse;
    const startedAt = Date.now();
    const maxWaitMs = 120000;
    let data: ChatResponse | null = null;
    while (Date.now() - startedAt < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const statusResponse = await fetch(`${apiBase}/api/census/chat/status/${startData.job_id}`);
      if (!statusResponse.ok) {
        throw new Error("Failed to fetch chat job status");
      }
      const statusData = (await statusResponse.json()) as ChatJobStatusResponse;
      const statusMessage = (statusData.latest_status?.message || "").trim();
      if (statusMessage) {
        onStatus(statusMessage);
      }
      if (statusData.state === "completed") {
        data = statusData.result?.payload ?? null;
        break;
      }
      if (statusData.state === "failed") {
        throw new Error(statusData.error || "Chat job failed");
      }
    }
    if (!data) {
      throw new Error("Chat job timed out");
    }
    const assistantText = (data.assistant_text || "").trim();
    const refusalText = (data.refusal_reason || "").trim();
    const text =
      refusalText && refusalText !== assistantText
        ? `${assistantText}\n\n${refusalText}`
        : assistantText;
    return [text, data.chart_url ?? null];
  };

  const sendMessage = async () => {
    if (!input.trim() || isSending) return;

    setIsSending(true);
    const userMsg = input;
    setInput("");

    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    inputRef.current?.focus();

    const updated: Message[] = [...messages, { role: "user", content: userMsg }];
    setMessages(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setMessages((prev) => [...prev, { role: "system", content: "/loading|Working..." }]);

    const setLoadingStatus = (status: string) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.content.startsWith("/loading")
            ? { ...message, content: `/loading|${status}` }
            : message
        )
      );
    };

    try {
      const [assistantText, chartUrl] = await getCensusMessage(userMsg, updated, setLoadingStatus);

      setMessages((prev) => {
        const filtered = prev.filter((message) => !message.content.startsWith("/loading"));
        const newMessages: Message[] = [
          ...filtered,
          {
            role: "system",
            type: "text",
            content: assistantText,
          },
        ];
        if (chartUrl) {
          newMessages.push({
            role: "system",
            type: "image",
            content: "",
            imagePath: chartUrl,
          });
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newMessages));
        return newMessages;
      });
    } catch {
      setMessages((prev) => {
        const filtered = prev.filter((message) => !message.content.startsWith("/loading"));
        const next = [
          ...filtered,
          {
            role: "system" as const,
            type: "text" as const,
            content: "Message failed to send. Check backend connection and try again.",
          },
        ];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <style>{css}</style>

      <div className={`app-container layout-${layoutVariant}`}>
        <div className="header">
          <div className="avatar">
            <img src="/snowflake-color.png" alt="Snowflake" />
          </div>
          <div className="header-text">
            <img className="title-logo" src="/censushublogo.png" alt="CensusHub" />
            {/* <span className="status">CensusHub is online</span> */}
          </div>
        </div>

        <div className="imessage-list" ref={chatRef}>
          <div className="spacer" />
          {messages.map((message, index) => {
            const isEmoji =
              /^\p{Extended_Pictographic}$/u.test(message.content.trim()) && message.content.trim().length <= 6;
            const bubbleClass = message.role === "user" ? "from-me" : "from-them";

            if (message.type === "image" && message.imagePath) {
              return (
                <div key={index} className="message-row from-them">
                  <img
                    src={message.imagePath}
                    alt="Census graph"
                    onLoad={scrollToBottomNow}
                    className="image-bubble"
                  />
                </div>
              );
            }

            if (message.content.startsWith("/loading")) {
              const statusText = message.content.split("|")[1] || "Working...";
              return (
                <div key={index} className="message-row from-them">
                  <div className="typing-container">
                    <p className="typing-indicator">
                      <span />
                      <span />
                      <span />
                    </p>
                    <p className="typing-status">{statusText}</p>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className={`message-row ${bubbleClass} no-tail`}>
                <p className={`${isEmoji ? "emoji" : ""}`}>{message.content}</p>
              </div>
            );
          })}
        </div>

        <div className="input-area">
          <input
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask anything about the census..."
            autoCapitalize="sentences"
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            enterKeyHint="send"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void sendMessage();
              }
            }}
          />
          <button
            type="button"
            className={input.trim() ? "active" : ""}
            onClick={() => void sendMessage()}
            style={{ backgroundColor: input.trim() ? "#007aff" : "#c7c7cc" }}
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}

export default App;

const css = `
@font-face {
  font-family: "SanFrancisco";
  src: url("https://cdn.rawgit.com/AllThingsSmitty/fonts/25983b71/SanFrancisco/sanfranciscodisplay-regular-webfont.woff2") format("woff2");
}

.imessage-list.keyboard-closing {
  pointer-events: none;
}

:root {
  --app-bg: #fff;
  --header-bg: rgba(245, 245, 247, 0.95);
  --bubble-blue: #007aff;
  --bubble-gray: #e9e9eb;
  --text-primary: #000;
  --text-white: #fff;
}

.image-bubble {
  max-width: 300px;
  border-radius: 18px;
  border-bottom-left-radius: 4px;
  object-fit: cover;
  background: #fff;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, "SanFrancisco", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: var(--app-bg);
  overflow: hidden;
  overscroll-behavior-y: none;
}

.app-container {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--app-bg);
  overflow: hidden;
}

.layout-1 .header {
  flex-direction: row;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 14px;
}

.layout-1 .header .avatar {
  margin-bottom: 0;
}

.layout-1 .header-text {
  text-align: left;
}

.layout-2 {
  max-width: min(560px, 96vw);
  margin: 10px auto;
  border: 1px solid #d9d9df;
  border-radius: 26px;
  box-shadow: 0 16px 28px rgba(0, 0, 0, 0.08);
}

.layout-2 .imessage-list {
  gap: 0.5rem;
}

.layout-3 .header {
  padding-bottom: 8px;
}

.layout-3 .message-row p {
  font-size: 14px;
  max-width: 66%;
}

.layout-3 .input-area input {
  min-height: 32px;
  font-size: 15px;
}

.layout-4 .header .avatar {
  width: 64px;
  height: 64px;
}

.layout-4 .message-row p {
  max-width: 82%;
  font-size: 17px;
}

.layout-4 .input-area {
  padding-left: 16px;
  padding-right: 16px;
}

.layout-5 {
  --app-bg: #0d1117;
  --header-bg: rgba(22, 27, 34, 0.95);
  --bubble-gray: #2f363d;
  --text-primary: #f0f6fc;
}

.layout-5 .header {
  border-bottom-color: #30363d;
}

.layout-5 .header h2 {
  color: #f0f6fc;
}

.layout-5 .header .status {
  color: #9ba5b2;
}

.layout-5 .input-area {
  background: #161b22;
  border-top-color: #30363d;
}

.layout-5 .input-area input {
  background: #0d1117;
  border-color: #30363d;
  color: #f0f6fc;
}

.layout-5 .input-area input::placeholder {
  color: #8b949e;
}

.header {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: max(env(safe-area-inset-top), 10px);
  padding-bottom: 10px;
  background: var(--header-bg);
  border-bottom: 1px solid #c6c6c8;
  backdrop-filter: blur(10px);
  z-index: 10;
}

.header .avatar {
  width: 50px;
  height: 50px;
  overflow: hidden;
  margin-bottom: 4px;
}

.header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-text {
  text-align: center;
}

.header h2 {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: #000;
}

.header .title-logo {
  display: block;
  width: 90px;
  max-width: 100%;
  margin: 0 auto;
  object-fit: contain;
}

.header .status {
  font-size: 12px;
  color: #8e8e93;
}

.imessage-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1rem;
  padding-bottom: calc(1rem + 56px + var(--keyboard-offset, 0px));
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  touch-action: pan-y;
}

.spacer {
  flex: 1;
  min-height: 1rem;
}

.message-row {
  display: flex;
  width: 100%;
  margin-bottom: 2px;
}

.message-row.from-me {
  justify-content: flex-end;
}

.message-row.from-them {
  justify-content: flex-start;
}

.message-row p {
  max-width: 75%;
  margin: 0;
  padding: 8px 16px;
  border-radius: 18px;
  font-size: 16px;
  line-height: 1.3;
  position: relative;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.from-me p {
  background-color: var(--bubble-blue);
  color: var(--text-white);
  border-bottom-right-radius: 4px;
}

.from-them p {
  background-color: var(--bubble-gray);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
}

.emoji {
  background: none !important;
  font-size: 3rem !important;
  padding: 0 !important;
}

.emoji::before {
  display: none !important;
}

.typing-indicator span {
  height: 8px;
  width: 8px;
  margin: 0 2px;
  background-color: #999;
  border-radius: 50%;
  display: inline-block;
  opacity: 0.4;
  animation: blink 1s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

.typing-container {
  background-color: var(--bubble-gray);
  color: var(--text-primary);
  border-radius: 18px;
  border-bottom-left-radius: 4px;
  padding: 10px 14px;
  max-width: 85%;
}

.typing-container .typing-indicator {
  margin: 0;
}

.typing-status {
  margin: 6px 0 0;
  font-size: 12px;
  color: #666;
  line-height: 1.3;
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

.input-area {
  padding: 8px 12px;
  padding-bottom: max(env(safe-area-inset-bottom), 12px);
  background: #f5f5f7;
  border-top: 1px solid #c6c6c8;
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-area input {
  flex: 1;
  border: 1px solid #c6c6c8;
  border-radius: 18px;
  padding: 8px 14px;
  font-size: 16px;
  line-height: 20px;
  outline: none;
  background: #fff;
  color: #000;
  min-height: 36px;
}

.input-area button {
  background-color: #c7c7cc;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.input-area button.active {
  background-color: var(--bubble-blue);
}
`;
